import React, { useState } from "react";
import { Box, Typography, Button, Popover } from "@mui/material";

const ProgressBar = ({ documents }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  if (!documents?.length) return null;

  const totalPages = documents.reduce((sum, doc) => {
    if (typeof doc.totalPages === "number") return sum + doc.totalPages;
    return sum + (doc.pages?.length || 0);
  }, 0);

  if (totalPages === 0) return null;

  const completedPages = documents.reduce((sum, doc) => {
    if (doc.statusCounts) return sum + (doc.statusCounts.completed || 0);
    return (
      sum +
      (doc.pages || []).filter(
        (p) => (p.status || "").toLowerCase().trim() === "completed"
      ).length
    );
  }, 0);

  const inReviewPages = documents.reduce((sum, doc) => {
    if (doc.statusCounts) return sum + (doc.statusCounts["in review"] || 0);
    return (
      sum +
      (doc.pages || []).filter(
        (p) => (p.status || "").toLowerCase().trim() === "in review"
      ).length
    );
  }, 0);

  const inProgressPages = documents.reduce((sum, doc) => {
    if (doc.statusCounts) return sum + (doc.statusCounts["in progress"] || 0);
    return (
      sum +
      (doc.pages || []).filter(
        (p) => (p.status || "").toLowerCase().trim() === "in progress"
      ).length
    );
  }, 0);

  const notStartedPages =
    totalPages - (completedPages + inReviewPages + inProgressPages);

  const completedPercent = Math.round((completedPages / totalPages) * 100);
  const inReviewPercent = Math.round((inReviewPages / totalPages) * 100);
  const inProgressPercent = Math.round((inProgressPages / totalPages) * 100);
  const notStartedPercent =
    100 - (completedPercent + inReviewPercent + inProgressPercent);

  const handleOpenLegend = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseLegend = () => {
    setAnchorEl(null);
  };

  const legendOpen = Boolean(anchorEl);

  const legendItemSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1,
  };

  const swatchBaseSx = {
    width: 16,
    height: 16,
    borderRadius: "4px",
    border: "1px solid rgba(0,0,0,0.12)",
    flexShrink: 0,
  };

  return (
    <Box sx={{ padding: "5px", mb: 2 }}>
      {" "}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {" "}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600 }}
        ></Typography>{" "}
        <Button
          size="small"
          variant="text"
          onClick={handleOpenLegend}
          sx={{
            textTransform: "none",
            minWidth: "auto",
            px: 0,
            fontWeight: 600,
            color: "#4b5563",
          }}
        >
          How to read this bar{" "}
        </Button>{" "}
      </Box>{" "}
      <Box
        sx={{
          width: "100%",
          height: "30px",
          display: "flex",
          borderRadius: "10px",
          overflow: "hidden",
          border: "1px solid #ccc",
        }}
      >
        {" "}
        <Box
          sx={{
            width: `${completedPercent}%`,
            backgroundColor: "#2e7d32",
            display: completedPercent > 0 ? "block" : "none",
          }}
        />{" "}
        <Box
          sx={{
            width: `${inReviewPercent}%`,
            backgroundColor: "#f9a825",
            backgroundImage: `repeating-linear-gradient(
       45deg,
       #f9a825,
       #f9a825 3px,
       white 3px,
       white 6px
      )`,
            display: inReviewPercent > 0 ? "block" : "none",
          }}
        />{" "}
        <Box
          sx={{
            width: `${inProgressPercent}%`,
            backgroundColor: "#42A5f5",
            display: inProgressPercent > 0 ? "block" : "none",
            backgroundImage: `radial-gradient(white 1px, transparent 1px)`,
            backgroundSize: "6px 6px",
          }}
        />{" "}
        <Box
          sx={{
            width: `${notStartedPercent}%`,
            backgroundColor: "#eceff1",
            display: notStartedPercent > 0 ? "block" : "none",
            backgroundImage: `repeating-linear-gradient(
       45deg,
       #eceff1,
       #eceff1 1px,
       white 1px,
       white 3px
      )`,
          }}
        />{" "}
      </Box>{" "}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "15px",
          mt: 1,
          flexWrap: "wrap",
        }}
      >
        {" "}
        <Typography
          variant="caption"
          sx={{ color: "#2e7d32", fontWeight: 700 }}
        >
          Completed: {completedPercent}%{" "}
        </Typography>{" "}
        <Typography
          variant="caption"
          sx={{ color: "#f9a825", fontWeight: 700 }}
        >
          In Review: {inReviewPercent}%{" "}
        </Typography>{" "}
      </Box>{" "}
      <Popover
        open={legendOpen}
        anchorEl={anchorEl}
        onClose={handleCloseLegend}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            p: 2,
            borderRadius: 2,
            maxWidth: 260,
          },
        }}
      >
        {" "}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Progress guide{" "}
        </Typography>{" "}
        <Box sx={legendItemSx}>
          {" "}
          <Box
            sx={{
              ...swatchBaseSx,
              backgroundColor: "#2e7d32",
            }}
          />
          <Typography variant="body2">Completed</Typography>{" "}
        </Box>{" "}
        <Box sx={legendItemSx}>
          {" "}
          <Box
            sx={{
              ...swatchBaseSx,
              backgroundColor: "#f9a825",
              backgroundImage: `repeating-linear-gradient(
        45deg,
        #f9a825,
        #f9a825 3px,
        white 3px,
        white 6px
       )`,
            }}
          />
          <Typography variant="body2">In Review</Typography>{" "}
        </Box>{" "}
        <Box sx={legendItemSx}>
          {" "}
          <Box
            sx={{
              ...swatchBaseSx,
              backgroundColor: "#42A5f5",
              backgroundImage: `radial-gradient(white 1px, transparent 1px)`,
              backgroundSize: "6px 6px",
            }}
          />
          <Typography variant="body2">In Progress</Typography>{" "}
        </Box>{" "}
        <Box sx={{ ...legendItemSx, mb: 0 }}>
          {" "}
          <Box
            sx={{
              ...swatchBaseSx,
              backgroundColor: "#eceff1",
              backgroundImage: `repeating-linear-gradient(
        45deg,
        #eceff1,
        #eceff1 1px,
        white 1px,
        white 3px
       )`,
            }}
          />
          <Typography variant="body2">Not Started</Typography>{" "}
        </Box>{" "}
      </Popover>{" "}
    </Box>
  );
};

export default ProgressBar;
